// client/src/components/GroupForm.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import '../App.css';
import { apiFetch } from '../lib/api';
import StartingBidEditor from './StartingBidEditor';
import { buildStartingBidSchedule } from '../shared/startingBid';
import { formatMoney, localeForCountry } from '../lib/money';

/* -------------------- Validation helpers (Option B) -------------------- */
const isHHMM = (s) => {
  if (typeof s !== 'string') return false;
  if (!/^\d{2}:\d{2}$/.test(s)) return false;
  const [h, m] = s.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59;
};

const minutesFromHHMM = (s) => {
  if (!isHHMM(s)) return NaN;
  const [h, m] = s.split(':').map(Number);
  return h * 60 + m;
};

function normalizeInt(v) {
  if (v === '' || v == null) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : NaN;
}

function normalizeNum(v) {
  if (v === '' || v == null) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

// IMPORTANT: keep validation messages currency-agnostic to avoid hardcoding symbols/codes.
// (UI will format amounts using group.currency_code)
function validateGroupForm({ form, startingBids, isEdit }) {
  const errors = {};

  const name = String(form.name || '').trim();
  if (!name) errors.name = 'Group name is required.';
  else if (name.length < 3) errors.name = 'Group name must be at least 3 characters.';

  // Country + currency required (no hardcoding)
  const cc = String(form.country_code || '').trim().toUpperCase();
  if (!cc || !/^[A-Z]{2}$/.test(cc)) errors.country_code = 'Country is required.';
  const cur = String(form.currency_code || '').trim().toUpperCase();
  if (!cur || !/^[A-Z]{3}$/.test(cur)) errors.currency_code = 'Currency is required.';

  const duration = normalizeInt(form.duration);
  if (!Number.isFinite(duration) || duration <= 0) errors.duration = 'Duration must be a whole number greater than 0.';

  const freq = String(form.contribution_frequency || '').toLowerCase();
  if (!freq) errors.contribution_frequency = 'Please select a contribution frequency.';
  else if (!['weekly', 'fortnightly', 'monthly'].includes(freq)) errors.contribution_frequency = 'Invalid frequency.';

  // Payment due days (days after the period ends) — max by frequency
  if (['weekly', 'fortnightly', 'monthly'].includes(freq)) {
    const maxDue = freq === 'weekly' ? 6 : freq === 'fortnightly' ? 13 : 27;
    const dueDays = normalizeInt(form.payment_due_days);
    if (!Number.isFinite(dueDays) || dueDays < 0) {
      errors.payment_due_days = 'Payment due days must be a whole number 0 or greater.';
    } else if (dueDays > maxDue) {
      errors.payment_due_days = `For ${freq} groups, payment due days cannot exceed ${maxDue}.`;
    }
  }

  const startDateStr = String(form.auction_start_date || '').trim();
  if (!startDateStr) {
    errors.auction_start_date = 'First auction date is required.';
  } else {
    const dt = DateTime.fromISO(startDateStr, { zone: 'utc' });
    if (!dt.isValid) {
      errors.auction_start_date = 'Invalid date.';
    } else {
      // Monthly safety – avoid 28/29/30/31 causing schedule drift
      if (freq === 'monthly' && dt.day > 27) {
        errors.auction_start_date = 'For monthly groups, please choose a date on or before the 27th.';
      }
      // Creation only: avoid past start dates
      if (!isEdit) {
        const today = DateTime.utc().startOf('day');
        if (dt < today) errors.auction_start_date = 'First auction date cannot be in the past.';
      }
    }
  }

  const contribution = normalizeNum(form.contribution);
  if (!Number.isFinite(contribution) || contribution <= 0) errors.contribution = 'Contribution must be greater than 0.';

  const bidStep = normalizeInt(form.bid_step);
  if (!Number.isFinite(bidStep) || bidStep <= 0) errors.bid_step = 'Minimum bid step must be a whole number greater than 0.';
  else if (bidStep % 10 !== 0) errors.bid_step = 'Minimum bid step must be a multiple of 10.';

  const organiserPeriod = normalizeInt(form.organiser_benefit_period);
  if (!Number.isFinite(organiserPeriod) || organiserPeriod <= 0) {
    errors.organiser_benefit_period = 'Organiser benefit period must be at least 1.';
  } else if (Number.isFinite(duration) && organiserPeriod > duration) {
    errors.organiser_benefit_period = 'Organiser benefit period cannot exceed duration.';
  }

  const organiserMemberships = normalizeInt(form.organiser_memberships);
  if (!Number.isFinite(organiserMemberships) || organiserMemberships <= 0) {
    errors.organiser_memberships = 'Organiser memberships must be at least 1.';
  } else {
    if (organiserMemberships > 3) errors.organiser_memberships = 'Organiser memberships cannot exceed 3.';
    if (Number.isFinite(duration) && organiserMemberships > duration) {
      errors.organiser_memberships = 'Organiser memberships cannot exceed duration.';
    }
  }

  const ratePct = normalizeNum(form.annual_interest_rate_percent);
  if (!Number.isFinite(ratePct) || ratePct < 0 || ratePct > 100) {
    errors.annual_interest_rate_percent = 'Annual interest rate must be between 0 and 100.';
  }

  const st = String(form.start_time || '').trim();
  const et = String(form.end_time || '').trim();
  if (!isHHMM(st)) errors.start_time = 'Start time must be in HH:MM format.';
  if (!isHHMM(et)) errors.end_time = 'End time must be in HH:MM format.';
  if (isHHMM(st) && isHHMM(et)) {
    if (minutesFromHHMM(st) >= minutesFromHHMM(et)) {
      errors.end_time = 'End time must be after start time.';
    }
  }

  const tz = String(form.timezone || '').trim();
  if (!tz) errors.timezone = 'Time zone is required.';

  // Starting bid schedule validation
  if (!Number.isFinite(duration) || duration <= 0) {
    // If duration invalid, don’t attempt schedule validation.
  } else {
    const rows = Array.isArray(startingBids) ? startingBids : [];
    if (rows.length !== duration) {
      errors.schedule = 'Starting bid schedule must have a row for every period. Click “Generate recommended schedule”.';
    } else {
      const seen = new Set();
      let bad = '';
      for (const r of rows) {
        const p = normalizeInt(r?.period);
        if (!Number.isFinite(p) || p < 1 || p > duration) {
          bad = 'Schedule has an invalid period number.';
          break;
        }
        if (seen.has(p)) {
          bad = 'Schedule has duplicate period numbers.';
          break;
        }
        seen.add(p);
        const amt = normalizeNum(r?.starting_bid);
        if (!Number.isFinite(amt) || amt < 0) {
          bad = 'Schedule contains an invalid starting bid amount.';
          break;
        }
        if (Number.isFinite(bidStep) && bidStep > 0) {
          const isWhole = Math.abs(amt - Math.round(amt)) < 1e-9;
          if (isWhole && (Math.round(amt) % bidStep !== 0)) {
            bad = `Each starting bid must be a multiple of the bid step (bid step: ${bidStep}).`;
            break;
          }
        }
      }
      if (!bad) {
        const map = new Map(rows.map((r) => [normalizeInt(r.period), normalizeNum(r.starting_bid)]));
        const benefitAmt = map.get(organiserPeriod);
        const lastAmt = map.get(duration);
        if (Number.isFinite(organiserPeriod) && organiserPeriod >= 1 && organiserPeriod <= duration) {
          if (!Number.isFinite(benefitAmt) || benefitAmt !== 0) {
            bad = 'Organiser benefit period must have a starting bid of 0.';
          }
        }
        if (!bad && (!Number.isFinite(lastAmt) || lastAmt !== 0)) {
          bad = 'Last period must have a starting bid of 0.';
        }
      }
      if (bad) errors.schedule = bad;
    }
  }

  return errors;
}
/* ---------------------------------------------------------------------- */

const getTimezoneOptions = () => {
  const zones = Intl.supportedValuesOf?.('timeZone') || [DateTime.local().zoneName];
  const common = [
    'Australia/Perth',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Darwin',
    'Australia/Hobart',
  ];
  const list = [...new Set([...common, ...zones])];
  return list
    .filter(Boolean)
    .map((tz) => ({ value: tz, label: tz }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Minimal fallbacks (UI dropdown still works if /api/meta is unavailable)
const FALLBACK_COUNTRIES = [
  { code: 'AU', name: 'Australia', default_currency_code: 'AUD' },
  { code: 'NZ', name: 'New Zealand', default_currency_code: 'NZD' },
  { code: 'US', name: 'United States', default_currency_code: 'USD' },
  { code: 'CA', name: 'Canada', default_currency_code: 'CAD' },
  { code: 'GB', name: 'United Kingdom', default_currency_code: 'GBP' },
  { code: 'IE', name: 'Ireland', default_currency_code: 'EUR' },
  { code: 'IN', name: 'India', default_currency_code: 'INR' },

  // Middle East
  { code: 'AE', name: 'United Arab Emirates', default_currency_code: 'AED' },
  { code: 'SA', name: 'Saudi Arabia', default_currency_code: 'SAR' },
  { code: 'QA', name: 'Qatar', default_currency_code: 'QAR' },
  { code: 'KW', name: 'Kuwait', default_currency_code: 'KWD' },
  { code: 'BH', name: 'Bahrain', default_currency_code: 'BHD' },
  { code: 'OM', name: 'Oman', default_currency_code: 'OMR' },
  { code: 'JO', name: 'Jordan', default_currency_code: 'JOD' },
  { code: 'TR', name: 'Turkey', default_currency_code: 'TRY' },
  { code: 'IL', name: 'Israel', default_currency_code: 'ILS' },
];

const FALLBACK_CURRENCIES = [
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'EUR', name: 'Euro' },
  { code: 'INR', name: 'Indian Rupee' },

  // Middle East
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'ILS', name: 'Israeli New Shekel' },
];

export default function GroupForm({ initialData = {}, onSubmit, onCancel, showTitle = true, title }) {
  const formRef = useRef(null);
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const [countryOptions, setCountryOptions] = useState(FALLBACK_COUNTRIES);
  const [currencyOptions, setCurrencyOptions] = useState(FALLBACK_CURRENCIES);

  const [form, setForm] = useState({
    id: initialData.id || '',
    name: initialData.name || '',
    description: initialData.description || '',

    // NEW: group-level country + currency
    country_code: initialData.country_code || initialData.countryCode || '',
    currency_code: initialData.currency_code || initialData.currencyCode || '',

    payment_due_days:
      initialData.payment_due_days != null
        ? Number(initialData.payment_due_days)
        : initialData.paymentDueDays != null
          ? Number(initialData.paymentDueDays)
          : '',

    duration: initialData.duration || '',
    contribution_frequency: initialData.contribution_frequency || '',
    auction_start_date: initialData.auction_start_date || '',
    contribution: initialData.contribution || '',
    bid_decrement: initialData.bid_decrement || 0,
    bid_step: initialData.bid_step || 100,
    organiser_benefit_period: initialData.organiser_benefit_period || 2,
    organiser_memberships: initialData.organiser_memberships || 1,
    annual_interest_rate_percent:
      initialData.annual_interest_rate != null ? Number(initialData.annual_interest_rate) * 100 : 5,
    start_time: initialData.start_time || '',
    end_time: initialData.end_time || '',
    timezone: initialData.timezone || DateTime.local().zoneName,
  });

  const [startingBids, setStartingBids] = useState([]);
  const [dateWarning, setDateWarning] = useState('');

  // Inline validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isEdit = Boolean(form.id);

  // Fetch country/currency lists from backend (Phase 1 meta endpoints), fallback if unavailable
  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      try {
        const [cRes, curRes] = await Promise.all([
          apiFetch('/api/meta/countries'),
          apiFetch('/api/meta/currencies'),
        ]);

        const cJson = await cRes.json().catch(() => ({}));
        const curJson = await curRes.json().catch(() => ({}));

        if (cancelled) return;

        const countries = Array.isArray(cJson?.countries)
          ? cJson.countries.map((c) => ({
              code: c.code,
              name: c.name,
              default_currency_code: c.defaultCurrency || c.default_currency_code || c.default_currency || '',
            }))
          : null;

        const currencies = Array.isArray(curJson?.currencies)
          ? curJson.currencies.map((c) => ({
              code: c.code,
              name: c.name,
            }))
          : null;

        if (countries?.length) setCountryOptions(countries);
        if (currencies?.length) setCurrencyOptions(currencies);
      } catch {
        // ignore; fallbacks remain
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  const zeroLabel = useMemo(() => {
    const cc = String(form.currency_code || '').toUpperCase();
    const ctry = String(form.country_code || '').toUpperCase();
    return formatMoney(0, {
      currencyCode: cc,
      countryCode: ctry,
      locale: localeForCountry(ctry),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      fallback: '0',
    });
  }, [form.currency_code, form.country_code]);

  // Populate schedule overrides from initialData (edit mode)
  useEffect(() => {
    if (initialData?.schedule?.length) {
      const rows = initialData.schedule
        .map((r) => ({
          period: Number(r.period_number),
          starting_bid: Number(r.starting_bid || 0),
        }))
        .filter((r) => Number.isFinite(r.period));
      if (rows.length) setStartingBids(rows);
    }
  }, [initialData]);

  // Revalidate when user edits fields after a submit attempt
  useEffect(() => {
    if (!submitAttempted) return;
    const next = validateGroupForm({ form, startingBids, isEdit });
    setErrors(next);
  }, [form, startingBids, submitAttempted, isEdit]);

  const markTouched = useCallback((name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const shouldShowError = useCallback(
    (name) => (submitAttempted || touched[name]) && Boolean(errors[name]),
    [errors, submitAttempted, touched]
  );

  const inputClass = useCallback((name) => (shouldShowError(name) ? 'input-error' : ''), [shouldShowError]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Keep numbers numeric (but allow 0 for validation messages)
    const numericFields = new Set([
      'duration',
      'contribution',
      'bid_step',
      'payment_due_days',
      'organiser_benefit_period',
      'organiser_memberships',
      'annual_interest_rate_percent',
    ]);

    // Frequency change: clamp / default payment_due_days
    if (name === 'contribution_frequency') {
      const nextFreq = String(value || '').toLowerCase();
      const maxDue = nextFreq === 'weekly' ? 6 : nextFreq === 'fortnightly' ? 13 : 27;
      setForm((prev) => {
        const next = { ...prev, contribution_frequency: value };
        const raw = prev.payment_due_days;
        const cur = raw === '' || raw == null ? NaN : Number(raw);
        if (!Number.isFinite(cur)) next.payment_due_days = maxDue;
        else next.payment_due_days = Math.max(0, Math.min(maxDue, Math.trunc(cur)));
        return next;
      });
      return;
    }

    // Country change: if currency blank, auto-set to country's default (if known)
    if (name === 'country_code') {
      const nextCountry = String(value || '').toUpperCase();
      const found = countryOptions.find((c) => String(c.code).toUpperCase() === nextCountry);
      const impliedRaw =
        found?.default_currency_code ??
        found?.defaultCurrency ??
        found?.default_currency ??
        '';
      const implied = impliedRaw ? String(impliedRaw).toUpperCase() : '';

      setForm((prev) => {
        const next = { ...prev, country_code: value };
        if (!String(prev.currency_code || '').trim() && implied) next.currency_code = implied;
        return next;
      });

      return;
    }

    setForm((prev) => {
      if (!numericFields.has(name)) return { ...prev, [name]: value };
      if (value === '') return { ...prev, [name]: '' };
      const n = Number(value);
      if (!Number.isFinite(n)) return prev; // ignore non-numeric
      // Keep integers as integers for integer fields
      if (['duration', 'bid_step', 'payment_due_days', 'organiser_benefit_period', 'organiser_memberships'].includes(name)) {
        return { ...prev, [name]: Math.trunc(n) };
      }
      return { ...prev, [name]: n };
    });

    // Warn for monthly dates > 27
    if (name === 'auction_start_date') {
      const dt = DateTime.fromISO(value);
      if (dt.isValid && form.contribution_frequency === 'monthly' && dt.day > 27) {
        setDateWarning(
          'Warning: Monthly schedule may break on dates after the 27th. Please choose a date on or before the 27th.'
        );
      } else {
        setDateWarning('');
      }
    }
  };

  // Recommended schedule generator gating
  const canGenerate = useMemo(() => {
    const duration = Number(form.duration || 0);
    const contribution = Number(form.contribution || 0);
    const bidStep = Number(form.bid_step || 0);
    const freq = String(form.contribution_frequency || '').toLowerCase();
    const ratePct = Number(form.annual_interest_rate_percent || 0);
    return (
      Number.isFinite(duration) &&
      duration > 0 &&
      Number.isFinite(contribution) &&
      contribution > 0 &&
      Number.isFinite(bidStep) &&
      bidStep > 0 &&
      ['weekly', 'fortnightly', 'monthly'].includes(freq) &&
      Number.isFinite(ratePct)
    );
  }, [
    form.duration,
    form.contribution,
    form.bid_step,
    form.contribution_frequency,
    form.annual_interest_rate_percent,
  ]);

  const recommendedRows = useMemo(() => {
    if (!canGenerate) return [];
    try {
      const annualRateDecimal = Number(form.annual_interest_rate_percent || 0) / 100;
      const rows = buildStartingBidSchedule({
        duration: Number(form.duration),
        contribution: Number(form.contribution),
        frequency: String(form.contribution_frequency || '').toLowerCase(),
        bidStep: Number(form.bid_step),
        annualRate: annualRateDecimal,
        organiserPeriod: Number(form.organiser_benefit_period || 0),
        rounding: 'ceil',
      });
      return rows.map((r) => ({ period: r.period, starting_bid: Number(r.recommended || 0) }));
    } catch {
      return [];
    }
  }, [
    canGenerate,
    form.duration,
    form.contribution,
    form.contribution_frequency,
    form.bid_step,
    form.annual_interest_rate_percent,
    form.organiser_benefit_period,
  ]);

  // If the organiser hasn’t edited yet, keep the editor in sync with recommendations
  useEffect(() => {
    if (!startingBids || startingBids.length === 0) {
      setStartingBids(recommendedRows);
    }
  }, [recommendedRows, startingBids]);

  const handleGenerateClick = useCallback(() => {
    setStartingBids(recommendedRows);
    markTouched('schedule');
  }, [recommendedRows, markTouched]);

  const scrollToFirstError = useCallback((errs) => {
    const order = [
      'name',
      'country_code',
      'currency_code',
      'duration',
      'contribution_frequency',
      'payment_due_days',
      'auction_start_date',
      'contribution',
      'bid_step',
      'organiser_benefit_period',
      'organiser_memberships',
      'annual_interest_rate_percent',
      'start_time',
      'end_time',
      'timezone',
      'schedule',
    ];
    const first = order.find((k) => errs[k]);
    if (!first) return;
    if (first === 'schedule') {
      const el = document.getElementById('starting-bid-schedule');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const input = formRef.current?.querySelector(`[name="${first}"]`);
    if (input && typeof input.focus === 'function') {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setSubmitError('');

    const nextErrors = validateGroupForm({ form, startingBids, isEdit });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      scrollToFirstError(nextErrors);
      return;
    }

    // 🆕 build scheduleOverrides payload (period → starting_bid)
    const scheduleOverrides = (startingBids || []).map((row) => ({
      period_number: Number(row.period),
      starting_bid: Number(row.starting_bid || 0),
    }));

    // convert percent → decimal for API
    const annual_interest_rate = Number(form.annual_interest_rate_percent || 0) / 100;

    const payload = {
      ...form,
      annual_interest_rate,
      scheduleOverrides,
    };
    delete payload.annual_interest_rate_percent;
    delete payload.bid_decrement;

    setSubmitting(true);
    try {
      await Promise.resolve(onSubmit(payload));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err?.message || 'Failed to submit.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="group-form" onSubmit={handleSubmit} ref={formRef}>
      {showTitle && <h3 className="page-title">{title ?? (form.id ? 'Edit Group' : 'Create Group')}</h3>}

      {submitError && (
        <div className="form-error-summary" role="alert">
          {submitError}
        </div>
      )}

      <label>Group Name</label>
      <input
        name="name"
        value={form.name}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('name')}
        required
      />
      {shouldShowError('name') && <div className="field-error">{errors.name}</div>}

      <label>Description</label>
      <textarea
        name="description"
        value={form.description}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
      />

      {/* NEW: Country + Currency */}
      <label>Country</label>
      <select
        name="country_code"
        value={form.country_code}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('country_code')}
        required
      >
        <option value="">-- Select Country --</option>
        {countryOptions.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>
      {shouldShowError('country_code') && <div className="field-error">{errors.country_code}</div>}

      <label>Currency</label>
      <select
        name="currency_code"
        value={form.currency_code}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('currency_code')}
        required
      >
        <option value="">-- Select Currency --</option>
        {currencyOptions.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name} ({c.code})
          </option>
        ))}
      </select>
      {shouldShowError('currency_code') && <div className="field-error">{errors.currency_code}</div>}

      <label>Duration (number of periods)</label>
      <input
        type="number"
        name="duration"
        value={form.duration}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('duration')}
        required
        min="1"
      />
      {shouldShowError('duration') && <div className="field-error">{errors.duration}</div>}

      <label>Contribution Frequency</label>
      <select
        name="contribution_frequency"
        value={form.contribution_frequency}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('contribution_frequency')}
        required
      >
        <option value="">-- Select Frequency --</option>
        <option value="weekly">Weekly</option>
        <option value="fortnightly">Fortnightly</option>
        <option value="monthly">Monthly</option>
      </select>
      {shouldShowError('contribution_frequency') && <div className="field-error">{errors.contribution_frequency}</div>}

      <label>Payment Due Days (after bidding ends)</label>
      <input
        type="number"
        name="payment_due_days"
        value={form.payment_due_days}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('payment_due_days')}
        required
        min="0"
        max={
          String(form.contribution_frequency || '').toLowerCase() === 'weekly'
            ? 6
            : String(form.contribution_frequency || '').toLowerCase() === 'fortnightly'
              ? 13
              : String(form.contribution_frequency || '').toLowerCase() === 'monthly'
                ? 27
                : undefined
        }
        disabled={!form.contribution_frequency}
      />
      {form.contribution_frequency && (
        <div style={{ marginTop: 4, marginBottom: 10, color: '#666', fontSize: 13 }}>
          Max allowed: {String(form.contribution_frequency || '').toLowerCase() === 'weekly'
            ? 6
            : String(form.contribution_frequency || '').toLowerCase() === 'fortnightly'
              ? 13
              : 27}{' '}
          days.
        </div>
      )}
      {shouldShowError('payment_due_days') && <div className="field-error">{errors.payment_due_days}</div>}

      <label>First Auction Date</label>
      <input
        type="date"
        name="auction_start_date"
        value={form.auction_start_date}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('auction_start_date')}
        required
        disabled={!form.contribution_frequency}
      />
      {dateWarning && <p className="warning-text">{dateWarning}</p>}
      {shouldShowError('auction_start_date') && <div className="field-error">{errors.auction_start_date}</div>}

      <label>Contribution Amount</label>
      <input
        type="number"
        name="contribution"
        value={form.contribution}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('contribution')}
        required
        min="1"
      />
      {shouldShowError('contribution') && <div className="field-error">{errors.contribution}</div>}

      <label>Minimum Bid Amount (step)</label>
      <input
        type="number"
        name="bid_step"
        value={form.bid_step}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('bid_step')}
        required
        min="10"
        step="10"
      />
      {shouldShowError('bid_step') && <div className="field-error">{errors.bid_step}</div>}

      <label>Organiser Benefit Period</label>
      <input
        type="number"
        name="organiser_benefit_period"
        value={form.organiser_benefit_period}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('organiser_benefit_period')}
        required
        min="1"
        max={form.duration || undefined}
      />
      {shouldShowError('organiser_benefit_period') && <div className="field-error">{errors.organiser_benefit_period}</div>}

      <label>Organiser Memberships</label>
      <input
        type="number"
        name="organiser_memberships"
        value={form.organiser_memberships}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('organiser_memberships')}
        required
        min="1"
      />
      {shouldShowError('organiser_memberships') && <div className="field-error">{errors.organiser_memberships}</div>}

      <label>Annual Interest Rate (%)</label>
      <input
        type="number"
        name="annual_interest_rate_percent"
        step="0.1"
        min="0"
        max="100"
        value={form.annual_interest_rate_percent}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('annual_interest_rate_percent')}
        required
      />
      {shouldShowError('annual_interest_rate_percent') && (
        <div className="field-error">{errors.annual_interest_rate_percent}</div>
      )}

      <label>Bidding Start Time</label>
      <input
        type="time"
        name="start_time"
        value={form.start_time}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('start_time')}
        required
      />
      {shouldShowError('start_time') && <div className="field-error">{errors.start_time}</div>}

      <label>Bidding End Time</label>
      <input
        type="time"
        name="end_time"
        value={form.end_time}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('end_time')}
        required
      />
      {shouldShowError('end_time') && <div className="field-error">{errors.end_time}</div>}

      <label>Time Zone</label>
      <select
        name="timezone"
        value={form.timezone}
        onChange={handleChange}
        onBlur={(e) => markTouched(e.target.name)}
        className={inputClass('timezone')}
        required
      >
        {timezoneOptions.map((tz) => (
          <option key={tz.value} value={tz.value}>
            {tz.label}
          </option>
        ))}
      </select>
      {shouldShowError('timezone') && <div className="field-error">{errors.timezone}</div>}

      {/* Starting Bid Schedule (editable) */}
      <div id="starting-bid-schedule" style={{ marginTop: 24, marginBottom: 8, fontWeight: 600 }}>
        Starting Bid Schedule
      </div>
      <div className="text-sm" style={{ marginBottom: 8 }}>
        Click “Generate” to populate recommended values using the interest rate, frequency, and bid step.
        You can edit per-period values. The organiser benefit period and the last period will stay at {zeroLabel}.
      </div>

      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
        <button type="button" className="secondary-button" onClick={handleGenerateClick} disabled={!canGenerate}>
          Generate recommended schedule
        </button>
        <span className="text-xs text-gray-500">
          Using: duration={String(form.duration || '')}, contribution={String(form.contribution || '')}, frequency=
          {String(form.contribution_frequency || '')}, step={String(form.bid_step || '')}, interest=
          {(form.annual_interest_rate_percent ?? '').toString() + '%'}
        </span>
      </div>
      {shouldShowError('schedule') && <div className="field-error">{errors.schedule}</div>}

      <StartingBidEditor
        form={{
          duration: form.duration,
          contribution: form.contribution,
          contribution_frequency: form.contribution_frequency,
          bid_step: form.bid_step,
          organiser_benefit_period: form.organiser_benefit_period,
          annual_interest_rate: Number(form.annual_interest_rate_percent || 0) / 100,
          country_code: form.country_code,
          currency_code: form.currency_code,
        }}
        values={startingBids}
        setValues={(v) => {
          setStartingBids(v);
          markTouched('schedule');
        }}
        rounding="ceil"
        currency={String(form.currency_code || '').toUpperCase()}
        locale={localeForCountry(String(form.country_code || '').toUpperCase())}
      />

      <button type="submit" className="action-button" style={{ marginTop: 16 }} disabled={submitting}>
        {submitting ? 'Please wait…' : form.id ? 'Update Group' : 'Create Group'}
      </button>

      {form.id && (
        <button
          type="button"
          className="secondary-button"
          disabled={submitting}
          onClick={async () => {
            if (!window.confirm('Regenerate auction schedule with current settings?')) return;
            try {
              const res = await apiFetch(`/api/groups/regenerate-auctions/${form.id}`, { method: 'POST' });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
              alert('Auction schedule regenerated successfully.');
            } catch (err) {
              alert(err.message || 'Failed to regenerate schedule.');
            }
          }}
        >
          Regenerate Auction Schedule
        </button>
      )}

      <button type="button" className="action-button cancel-button" onClick={onCancel} disabled={submitting}>
        Cancel
      </button>
    </form>
  );
}
